import { useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import {
  optionNamespace,
  GroupOptions,
  TypePeriodOptions,
  type ModifierGroup,
  type ModifierConfig,
  type ColumnHandler,
  type ModifierOrigins,
  type DataFrameTypes,
  type Value,
} from '../../hooks/modifiers';

import Button from '../../juristec-ui/core/Button';
import InputTextLine from '../../juristec-ui/core/InputTextLine';
import Select from '../../juristec-ui/core/SelectNew';
import KpiFilter from '../KpiFilter';
import KpiFilterApplied from '../KpiFilterApplied';
import SelectTips from '../Popovers/SelectTips';

import { dateOptions } from '../../options';
import { trimString } from '../../juristec-ui/utils/functions/formatString';
import { verifyInput } from '../../juristec-ui/utils/validators/inputTextValidators';

import { SelectOption } from '../../types';
import { LabelSmall, ModifiersContainer } from './styled/KpiCreate.styled';

interface KpiModifier {
  modifiers: ModifierGroup;
  modifierType: 'filter' | 'group';
  atModal?: boolean;
  columnOptsMap?: Record<string, SelectOption[]>;
  getDateSelector: (origin: string) => SelectOption;
  modifierConfig: ModifierConfig;
  simpleOptions: SelectOption[];
  originOptions: SelectOption[];
  getColumnOptions: (columns: ColumnHandler) => SelectOption[];
  getSimpleOptions: (modifiers: ModifierGroup) => SelectOption[];
  handleModifierConfigs: (newData: Partial<ModifierConfig>) => void;
  configColumnIsDate: () => boolean;
  getOptionsByConfigGroup: () => SelectOption[];
  checkModifierValues: () => boolean;
  modifierOrigins: ModifierOrigins;
  addModifier: () => void;
  removeModifier: (origin: string, uid: string) => void;
  editModifier: (
    origin: string, uid: string, newValues: Value[], fType: string, newRule?: string,
  ) => void;
  getUniqueValues: (column: string, selector: string) => Promise<SelectOption[]>;
  changeModifierOrder: (origin: string, pos: number, varOp: 1|-1) => void;
  dataFrameTypes: DataFrameTypes;
}

interface GroupNameState {
  value: string;
  error: boolean;
  errorMsg: string;
}

const emptyObj = {};

const KpiModifier = ({
  modifiers,
  modifierType = 'filter',
  atModal = false,
  columnOptsMap = emptyObj,
  getDateSelector,
  modifierConfig,
  simpleOptions,
  originOptions,
  getColumnOptions,
  getSimpleOptions,
  handleModifierConfigs,
  configColumnIsDate,
  getOptionsByConfigGroup,
  checkModifierValues,
  modifierOrigins,
  addModifier,
  removeModifier,
  editModifier,
  getUniqueValues,
  changeModifierOrder,
  dataFrameTypes,
}: KpiModifier) => {
  const { t } = useTranslation();

  const [groupName, setGroupName] = useState<GroupNameState>({ value: '', error: true, errorMsg: '' });

  const handleGetColumnOptions = () => getColumnOptions(columnOptsMap);

  const handleColumnSelect = (selected: SelectOption) => handleModifierConfigs({
    column: selected, selector: getDateSelector(selected.origin?.[0]),
  });

  const handleGroupSelect = (selected: SelectOption) => handleModifierConfigs({
    group: selected,
  });

  const handleOriginSelect = (selected: SelectOption) => handleModifierConfigs({
    origin: selected, selector: getDateSelector(selected.value),
  });

  const handleSelectorSelect = (selected: SelectOption) => handleModifierConfigs({
    selector: selected,
  });

  const handleFormatSelect = (selected: SelectOption) => handleModifierConfigs({
    format: selected,
  });

  const handleGroupChange = (values: Value[]) => handleModifierConfigs({ values });

  const handleGroupName = (value: string) => {
    const msg = verifyInput(value, true);
    setGroupName({
      value,
      error: msg.length !== 0,
      errorMsg: msg,
    });
    if (msg.length === 0) {
      handleModifierConfigs({ rule: value });
    }
  };

  const handleAddModifier = () => {
    addModifier();
    setGroupName({ value: '', error: true, errorMsg: '' });
  };

  return (
    <>
      <div className="gap-fix-first">
        <div className="gap-fix-second">
          <div style={{ width: '50%', flex: '1', minWidth: '0' }}>
            <Select
              atModal={atModal}
              label={modifierType === 'filter' ? 'Filtrar por' : 'Agrupar por'}
              onChange={handleColumnSelect}
              // @ts-expect-error not typed yet
              value={modifierConfig.column}
              options={handleGetColumnOptions()}
              sortBy="ascending"
              searchable
              clearable
            />
          </div>
          <div style={{ width: '50%', flex: '1', minWidth: '0' }}>
            <Select
              atModal={atModal}
              label="Tipo"
              onChange={handleGroupSelect}
              // @ts-expect-error not typed yet
              value={modifierConfig.group}
              options={GroupOptions.list}
              optionNamespace={optionNamespace}
            />
          </div>
        </div>
        {(modifierConfig.column?.value?.length ?? 0) > 0 && (
          <>
            <div className="gap-fix-second">
              {modifierType === 'group' ? (
                <>
                  {originOptions.length > 1 && (
                    <div style={
                      originOptions.length > 1 ? { width: '50%', flex: '1', minWidth: '0' } : {
                        pointerEvents: 'none',
                        width: '100%',
                        opacity: '0.3',
                      }
                    }
                    >
                      <Select
                        atModal={atModal}
                        label="Origem"
                        onChange={handleOriginSelect}
                        // @ts-expect-error not typed yet
                        value={modifierConfig.origin}
                        options={originOptions}
                        formatOptionLabel={false}
                        searchable
                      />
                    </div>
                  )}
                </>
              ) : (
                <>
                  {modifierConfig.group.value === GroupOptions.enum.SIMPLE
                    && configColumnIsDate() && (
                    <div style={{ width: '50%', flex: '1', minWidth: '0' }}>
                      <Select
                        atModal={atModal}
                        label="Formato"
                        placeholder="Selecione"
                        onChange={handleSelectorSelect}
                        // @ts-expect-error not typed yet
                        value={modifierConfig.selector}
                        options={dateOptions}
                        searchable
                      />
                    </div>
                  )}
                </>
              )}
              <div style={{
                position: 'relative', width: '100%', flex: '1', minWidth: '0',
              }}
              >
                <Select
                  atModal={atModal}
                  label="Condição"
                  onChange={handleFormatSelect}
                  // @ts-expect-error not typed yet
                  value={modifierConfig.format}
                  options={getOptionsByConfigGroup()}
                  optionNamespace={optionNamespace}
                  searchable
                />
                {modifierConfig.format.value !== '' && (
                  <SelectTips
                    // @ts-expect-error not typed yet
                    description={t(modifierConfig.format.description, {
                      ns: optionNamespace,
                      ...(modifierConfig.format.params ?? {}),
                    })}
                    style={{ top: '5px', right: '5px' }}
                  />
                )}
              </div>
            </div>
            {modifierConfig.format?.value.length > 0 && (
              modifierConfig.group?.value === GroupOptions.enum.ADVANCED || simpleOptions.length > 0
            ) && (
              <>
                {!Object.keys(TypePeriodOptions.map).includes(modifierConfig.format.value) && (
                  <KpiFilter
                    atModal={atModal}
                    filterType={modifierConfig.format.value}
                    // @ts-expect-error not typed yet
                    handle={handleGroupChange}
                    // @ts-expect-error not typed yet
                    values={modifierConfig.values}
                    // @ts-expect-error not typed yet
                    options={getSimpleOptions(modifiers)}
                    format={modifierConfig.column?.type}
                  />
                )}
                {modifierType === 'group' && (
                  <InputTextLine
                    label="Nome do Agrupamento"
                    type="text"
                    style={{ marginBottom: 0 }}
                    onChange={(e) => handleGroupName(e.target.value)}
                    value={groupName.value}
                    errorMessage={groupName.errorMsg}
                    error={groupName.errorMsg.length > 0}
                    styleContainer={{ marginTop: '-8px' }}
                    onBlur={(e) => handleGroupName(trimString(e.target.value))}
                    setValue={() => {}}
                  />
                )}
                <Button
                  onClick={handleAddModifier}
                  color="secondary"
                  disabled={!checkModifierValues() || (
                    modifierType === 'group' && groupName.error
                  )}
                  style={{ margin: '15px 0 10px', width: '100%' }}
                >
                  {modifierType === 'filter' ? 'Adicionar filtro' : 'Adicionar agrupamento'}
                </Button>
              </>
            )}
          </>
        )}
      </div>
      {Object.keys(modifierOrigins).some((k) => modifiers[k]?.length > 0) && (
        <LabelSmall>
          {modifierType === 'filter' ? 'Filtros Aplicados' : 'Agrupamentos Aplicados'}
        </LabelSmall>
      )}
      {Object.entries(modifierOrigins).map(([k, l]) => (
        <Fragment key={`KpiFilterApplied-${k}`}>
          {modifiers[k]?.length > 0 && (
            <ModifiersContainer>
              {modifierType === 'group' && (
                <LabelSmall style={{ margin: '5px 0 -5px' }}>
                  {l}
                </LabelSmall>
              )}
              {modifiers[k].map((item, i) => (
                <KpiFilterApplied
                  atModal={atModal}
                  nameEdit={modifierType === 'group'}
                  key={item.uid}
                  origin={k}
                  modifier={item}
                  modifiers={modifiers}
                  removeFunc={removeModifier}
                  editFunc={editModifier}
                  getOptions={getUniqueValues}
                  dataFrameTypes={dataFrameTypes}
                  position={i}
                  isLast={modifiers[k].length - 1 === i}
                  changePosition={changeModifierOrder}
                />
              ))}
            </ModifiersContainer>
          )}
        </Fragment>
      ))}
    </>
  );
};

export default KpiModifier;
